import { createFileRoute } from '@tanstack/react-router'
import { Radio, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { MarketingChannel } from '@/api/admin-marketing-channels-types'
import type { MarketingSurfaceType } from '@/api/admin-marketing-content-types'
import { MarketingSurface } from '@/api/admin-marketing-content-types'
import { useCreateMarketingChannel, useDeleteMarketingChannel, useUpdateMarketingChannel } from '@/api/api-mutations'
import { useListMarketingChannels, useListPublishableKeys } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Combobox } from '@/components/base/combobox'
import { Container, ContainerHeader } from '@/components/base/container'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Text } from '@/components/base/text'
import { Textarea } from '@/components/base/textarea'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'

export const Route = createFileRoute('/_authenticated/settings/marketing-channels/')({
	component: MarketingChannelsPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.Channel.Title()
	}
})

// Labels for the surfaces a channel can serve, shown in the drawer multi-select and on each row.
const useSurfaceLabels = (): Record<MarketingSurfaceType, string> => {
	const { L } = useI18n()
	return {
		[MarketingSurface.CustomerAppFeed]: L.Marketing.Surface.CustomerAppFeed(),
		[MarketingSurface.CheckoutScreen]: L.Marketing.Surface.CheckoutScreen()
	}
}

function MarketingChannelsPage() {
	const { L } = useI18n()
	const surfaceLabels = useSurfaceLabels()
	const channelsQuery = useListMarketingChannels()

	const [editing, setEditing] = useState<MarketingChannel | null>(null)
	const [creating, setCreating] = useState(false)

	const channels = channelsQuery.data ?? []
	const showEmpty = channelsQuery.isSuccess && channels.length === 0

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Marketing.Channel.Title()}
					rightSide={
						<IconButton tooltipText={L.Marketing.Channel.NewChannel()} onClick={() => setCreating(true)}>
							<Plus />
						</IconButton>
					}
				/>
				<div className='flex-1 overflow-y-auto p-4 sm:p-6'>
					{channelsQuery.isError ? (
						<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
							<Heading level='h3'>{L.ApiErrorAlert.FailedToLoadData()}</Heading>
							<Button type='button' variant='secondary' onClick={() => channelsQuery.refetch()}>
								{L.General.TryAgain()}
							</Button>
						</div>
					) : showEmpty ? (
						<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
							<Radio className='size-10 text-ui-fg-muted' />
							<Heading level='h3'>{L.Marketing.Channel.NoChannels()}</Heading>
							<Text className='max-w-sm text-ui-fg-subtle'>
								{L.Marketing.Channel.NoChannelsDescription()}
							</Text>
							<Button onClick={() => setCreating(true)}>{L.Marketing.Channel.NewChannel()}</Button>
						</div>
					) : (
						<div className='flex flex-col gap-1.5'>
							{channels.map(channel => (
								<button
									key={channel.uuid}
									type='button'
									onClick={() => setEditing(channel)}
									className='group flex items-center gap-4 rounded-md border border-ui-border-base bg-ui-bg-base px-3 py-2.5 text-left hover:bg-ui-bg-base-hover'
								>
									<div className='flex flex-1 flex-col'>
										<Text className='font-medium'>{channel.name}</Text>
										<Text className='text-xs text-ui-fg-subtle'>
											{channel.surfaces.map(s => surfaceLabels[s]).join(', ') || '—'}
										</Text>
										<Text className='text-xs text-ui-fg-muted'>
											{channel.publishableKeys.map(k => k.name).join(', ') || '—'}
										</Text>
									</div>
								</button>
							))}
						</div>
					)}
				</div>
			</Container>

			{creating && <ChannelDrawer channel={null} onClose={() => setCreating(false)} />}
			{editing && <ChannelDrawer channel={editing} onClose={() => setEditing(null)} />}
		</SingleColumnPage>
	)
}

function ChannelDrawer({ channel, onClose }: { channel: MarketingChannel | null; onClose: () => void }) {
	const { L } = useI18n()
	const surfaceLabels = useSurfaceLabels()
	const isEdit = channel !== null

	const createChannel = useCreateMarketingChannel(true)
	const updateChannel = useUpdateMarketingChannel(true, channel?.uuid ?? '')
	const deleteChannel = useDeleteMarketingChannel(true)

	const publishableKeysQuery = useListPublishableKeys({})

	const [name, setName] = useState(channel?.name ?? '')
	const [description, setDescription] = useState(channel?.description ?? '')
	const initialKeyUuids = channel?.publishableKeys.map(k => k.uuid) ?? []
	const [publishableKeyUuids, setPublishableKeyUuids] = useState<string[]>(initialKeyUuids)
	// A new channel defaults to the customer-app feed (matching the column default); editing seeds
	// from the channel's declared surfaces.
	const [surfaces, setSurfaces] = useState<MarketingSurfaceType[]>(
		channel?.surfaces ?? [MarketingSurface.CustomerAppFeed]
	)

	const surfaceOptions = useMemo(
		() => Object.values(MarketingSurface).map(s => ({ value: s, label: surfaceLabels[s] })),
		[surfaceLabels]
	)

	// A publishable key belongs to at most one marketing channel, so offer keys that are either
	// unlinked or already linked to this channel; keys owned by another channel are hidden.
	const publishableKeyOptions = useMemo(
		() =>
			(publishableKeysQuery.data ?? [])
				.filter(k => !k.marketingChannel || k.marketingChannel.uuid === channel?.uuid)
				.map(k => ({ value: k.uuid, label: k.name })),
		[publishableKeysQuery.data, channel?.uuid]
	)

	// Radix Dialog controls its own open state; drive it so closing unmounts via onClose.
	const [open, setOpen] = useState(true)
	useEffect(() => {
		if (!open) onClose()
	}, [open, onClose])

	const handleSave = async () => {
		if (!name.trim()) return
		try {
			const result = isEdit
				? await updateChannel.mutateAsync({
						path: { marketingChannelUuid: channel.uuid },
						body: { name, description: description || null, surfaces, publishableKeyUuids }
					})
				: await createChannel.mutateAsync({
						body: { name, description: description || undefined, surfaces, publishableKeyUuids }
					})
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			setOpen(false)
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDelete = async () => {
		if (!isEdit) return
		if (!window.confirm(L.Marketing.Channel.DeleteConfirm())) return
		try {
			const result = await deleteChannel.mutateAsync({ path: { marketingChannelUuid: channel.uuid } })
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			setOpen(false)
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const isSaving = createChannel.isPending || updateChannel.isPending

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title asChild>
						<Heading>{isEdit ? channel.name : L.Marketing.Channel.NewChannel()}</Heading>
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className='flex flex-col gap-4 overflow-y-auto'>
					<label className='flex flex-col gap-1.5'>
						<Text className='text-xs font-medium text-ui-fg-subtle'>
							{L.Marketing.Channel.Field.Name()}
						</Text>
						<Input
							value={name}
							placeholder={L.Marketing.Channel.Field.NamePlaceholder()}
							onChange={e => setName(e.target.value)}
						/>
					</label>
					<label className='flex flex-col gap-1.5'>
						<Text className='text-xs font-medium text-ui-fg-subtle'>
							{L.Marketing.Channel.Field.Description()}
						</Text>
						<Textarea
							value={description}
							placeholder={L.Marketing.Channel.Field.DescriptionPlaceholder()}
							onChange={e => setDescription(e.target.value)}
						/>
					</label>
					<div className='flex flex-col gap-1.5'>
						<Text className='text-xs font-medium text-ui-fg-subtle'>
							{L.Marketing.Channel.Field.Surfaces()}
						</Text>
						<Combobox
							value={surfaces}
							onChange={value => setSurfaces((value as MarketingSurfaceType[]) ?? [])}
							options={surfaceOptions}
							placeholder={L.Marketing.Channel.Field.SurfacesPlaceholder()}
						/>
						<Text className='text-xs text-ui-fg-muted'>{L.Marketing.Channel.SurfacesHint()}</Text>
					</div>
					<div className='flex flex-col gap-1.5'>
						<Text className='text-xs font-medium text-ui-fg-subtle'>
							{L.Marketing.Channel.Field.PublishableKeys()}
						</Text>
						<Combobox
							value={publishableKeyUuids}
							onChange={value => setPublishableKeyUuids((value as string[]) ?? [])}
							options={publishableKeyOptions}
							optionsLoading={publishableKeysQuery.isLoading}
							placeholder={L.Marketing.Channel.Field.PublishableKeysPlaceholder()}
						/>
						<Text className='text-xs text-ui-fg-muted'>{L.Marketing.Channel.PublishableKeysHint()}</Text>
					</div>
				</Drawer.Body>
				<Drawer.Footer>
					{isEdit && (
						<IconButton
							variant='transparent'
							tooltipText={L.Marketing.Channel.Action.Delete()}
							onClick={handleDelete}
							disabled={deleteChannel.isPending}
							className='mr-auto'
						>
							<Trash2 className='size-4' />
						</IconButton>
					)}
					<Drawer.Close asChild>
						<Button variant='secondary'>{L.General.Cancel()}</Button>
					</Drawer.Close>
					<Button onClick={handleSave} disabled={isSaving || !name.trim()}>
						{isEdit ? L.Marketing.Channel.Action.Save() : L.Marketing.Channel.Action.Create()}
					</Button>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer>
	)
}
